import React, { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { Table, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    DisconnectOutlined,
    GlobalOutlined,
    PlayCircleOutlined,
    QuestionCircleOutlined,
    StopOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import { IDataTypeMain, INameObjectN, ISubscribersListItem } from '../../../interfaces';
import { useProfileFeatureFlags, useUiIsLoading, useUiSearch } from '../../../hooks';
import { useSubscribersTable } from '../../../hooks';
import { deleteSubscriber } from '../../../middleware';
import { lightText } from '../../../instruments';
import { cleanColumnsDataSource } from '../../../instruments/cleanColumnsDataSource';
import { tF, tI } from '../../../translate';
import { Catcher, LinkComponent, MultiFeaturesShowTags, TableItemToolbar, columnToolbar } from '../../../Components';
import '../../../customStyle/table.less';
import Styles from '../../styles/m_viewStyles.less';

type StatusType =
    | 'pre_active'
    | 'suspended'
    | 'terminated'
    | 'pending'
    | 'ready'
    | 'active'
    | 'unknown'
    | 'live'
    | 'unmanaged';

interface IStatusConfigType {
    color: string;
    icon: JSX.Element;
    label: string;
}

const statusConfig: Record<StatusType, IStatusConfigType> = {
    pre_active: {
        color: '#87d068',
        icon: <PlayCircleOutlined />,
        label: 'Pre-active',
    },
    suspended: {
        color: '#f50',
        icon: <StopOutlined />,
        label: 'Suspended',
    },
    terminated: {
        color: '#ff4d4f',
        icon: <CloseCircleOutlined />,
        label: 'Terminated',
    },
    pending: {
        color: '#faad14',
        icon: <ClockCircleOutlined />,
        label: 'Pending',
    },
    ready: {
        color: '#1890ff',
        icon: <CheckCircleOutlined />,
        label: 'Ready',
    },
    active: {
        color: '#52c41a',
        icon: <ThunderboltOutlined />,
        label: 'Active',
    },
    unknown: {
        color: '#848484',
        icon: <QuestionCircleOutlined />,
        label: 'Unknown',
    },
    live: {
        color: '#722ed1',
        icon: <GlobalOutlined />,
        label: 'Live',
    },
    unmanaged: {
        color: '#595959',
        icon: <DisconnectOutlined />,
        label: 'Unmanaged',
    },
};

interface IStatusDisplayProps {
    status: string;
}

type IDataType = IDataTypeMain &
    Omit<
        ISubscribersListItem,
        | 'activeThisMonth'
        | 'companies'
        | 'features'
        | 'iccId'
        | 'imsi'
        | 'msisdn'
        | 'operator'
        | 'operatorId'
        | 'operatorName'
        | 'statuses'
    > & {
        companies?: ReactNode;
        deviceType?: ReactNode;
        features?: ReactNode;
        iccId?: ReactNode;
        id: string;
        imsiLink?: ReactNode;
        msisdn?: ReactNode;
        operatorLink?: ReactNode;
        statuses?: ReactNode;
    };

const StatusDisplay: React.FC<IStatusDisplayProps> = ({ status }) => {
    const config = statusConfig[status.toLowerCase() as StatusType] || statusConfig.unknown;
    return (
        <Tag color={config.color} icon={config.icon}>
            {config.label}
        </Tag>
    );
};

export const SubscribersTableList: React.FC = observer(() => {
    const isLoading = useUiIsLoading();
    const isSubscribersFeatures = useProfileFeatureFlags()?.subscribersFeatures;
    const list = useSubscribersTable();
    const search = useUiSearch();

    const tableColumns: ColumnsType<IDataType> = [
        {
            title: tF('IMSI'),
            dataIndex: 'imsiLink',
        },
        {
            title: tF('Operator'),
            dataIndex: 'operatorLink',
        },
        {
            title: tF('ICCID'),
            dataIndex: 'iccId',
        },
        {
            title: tF('Device name'),
            dataIndex: 'deviceName',
        },
        {
            title: tF('Device type'),
            dataIndex: 'deviceType',
        },
        {
            title: tF('MSISDN'),
            dataIndex: 'msisdn',
        },
        {
            title: tF('Companies'),
            dataIndex: 'companies',
        },
        {
            title: tF('Statuses'),
            dataIndex: 'statuses',
        },
        isSubscribersFeatures
            ? {
                  title: tF('Features'),
                  dataIndex: 'features',
              }
            : {},
        columnToolbar,
    ];

    const tableData: IDataType[] = list?.map((s: ISubscribersListItem) => {
        const deleteItem = (): void => {
            deleteSubscriber(s.id);
        };
        const imsi = s.imsi ? s.imsi : `# ${s.id}`;
        // const activeThisMonth = (
        //     <Tooltip placement="top" title={tI(c.activeThisMonth ? 'Active this month' : 'Not active this month')}>
        //         {c.activeThisMonth ? (
        //             <CheckCircleOutlined className={cx(Styles.statusIcon, Styles.statusIconActive)} />
        //         ) : (
        //             <MinusCircleOutlined className={cx(Styles.statusIcon, Styles.statusIconPassive)} />
        //         )}
        //     </Tooltip>
        // );

        const CompaniesListLinks = s.companies?.map((c: INameObjectN, i: number) => (
            <LinkComponent currentListComponent={'CompaniesList'} id={c.id} name={c.name} key={`${c.id}-${i}`} />
        ));

        return {
            key: s.id,
            id: s.id,
            companies: CompaniesListLinks,
            deviceName: lightText(s.deviceInfo?.name || s.deviceInfo?.marketingName, search),
            deviceType: lightText(s.deviceInfo?.deviceType, search),
            features: isSubscribersFeatures ? <MultiFeaturesShowTags values={s.features} /> : null,
            iccId: lightText(s.iccId, search),
            imsiLink: <LinkComponent currentListComponent={'SubscribersList'} id={s.id} isLight name={s.imsi} />,
            msisdn: lightText(s.msisdn, search),
            operatorLink: (
                <LinkComponent currentListComponent={'OperatorsList'} id={s.operatorId} isLight name={s.operatorName} />
            ),
            statuses: s?.statuses?.map((status: string, index) => {
                return (
                    <span key={index}>
                        <StatusDisplay status={status} />
                    </span>
                );
            }),
            toolbar: <TableItemToolbar deleteItem={deleteItem} id={s.id} itemName={`"${imsi}" ${tI('subscriber')}`} />,
        };
    });

    return (
        <Catcher>
            <div className={Styles.tablePlace}>
                <Table
                    className={'table-theme'}
                    {...cleanColumnsDataSource<IDataType>(tableColumns, tableData)}
                    loading={isLoading}
                    pagination={false}
                    size={'middle'}
                />
            </div>
        </Catcher>
    );
});
